// An app's full view: a sandboxed iframe on the app frame origin (a different site from Bloom), connected to the
// room only through the SDK bridge (apps/bridge.ts). Apps made with Bloom (and their drafts) load their own view
// module there, by bundle id; a new version means a new frame. If the frame origin isn't set up, it says so instead of ever
// serving app code from Bloom's own origin. If the frame navigates away from its page, it's torn down.
import { useEffect, useRef, useState } from "react";
import { FRAME_SANDBOX, type AppManifest, type SdkMember, type SdkUser } from "@bloom/shared";
import { connectFrame, currentTheme } from "../apps/bridge.ts";
import { configFailureText, getConfig } from "../upload.ts";
import { useTheme } from "../theme.ts";
import type { Room } from "../realtime.ts";

interface Props {
  app: AppManifest;
  room: Room;
  user: SdkUser;
  members: () => SdkMember[];
  channelId: () => string | null;
  route: Record<string, string>;
  openApp: (slug: string, route?: Record<string, string>) => void;
}

export function AppFrame({ app, room, user, members, channelId, route, openApp }: Props) {
  // undefined: asking /api/config. null: it loaded and has no frame origin ("not set up"). `failure`: it did not load.
  const [origin, setOrigin] = useState<string | null | undefined>(undefined);
  const [failure, setFailure] = useState<string | null>(null);
  const [left, setLeft] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);
  const bridge = useRef<ReturnType<typeof connectFrame> | null>(null);
  const loads = useRef(0);
  const { resolved } = useTheme();
  // The latest props, for the bridge (connected once per frame).
  const latest = useRef({ members, channelId, openApp });
  latest.current = { members, channelId, openApp };

  const loadOrigin = () => {
    setFailure(null);
    setOrigin(undefined);
    void getConfig().then((c) => setOrigin(c.appFrameOrigin ?? null), (e) => setFailure(configFailureText(e)));
  };
  useEffect(loadOrigin, []);

  useEffect(() => {
    const el = frame.current;
    if (!el || !origin || left) return;
    loads.current = 0;
    setLoaded(false);
    const b = connectFrame(el, {
      app,
      room,
      user,
      members: () => latest.current.members(),
      channelId: () => latest.current.channelId(),
      route,
      openApp: (s, r) => latest.current.openApp(s, r),
    });
    bridge.current = b;
    return () => {
      b.dispose();
      bridge.current = null;
    };
  }, [origin, app.slug, app.bloom?.bundle, room, left]);

  // Theme changes reach the frame live (the iframe's own CSS reads the variables we send).
  useEffect(() => {
    requestAnimationFrame(() => bridge.current?.theme(currentTheme()));
  }, [resolved]);
  const routeKey = JSON.stringify(route);
  useEffect(() => {
    if (loaded) bridge.current?.route(route);
  }, [routeKey]);

  if (failure) {
    return (
      <div className="app-empty" role="alert">
        <div className="app-empty-icon" aria-hidden="true">{app.icon}</div>
        <h2>App views didn't load</h2>
        <p className="muted">{failure}</p>
        <button type="button" className="btn btn-small" onClick={loadOrigin}>Try again</button>
      </div>
    );
  }
  if (origin === undefined) return <div className="app-frame-wrap" aria-busy="true" />;
  if (!origin) {
    return (
      <div className="app-empty" role="status">
        <div className="app-empty-icon" aria-hidden="true">{app.icon}</div>
        <h2>App views aren't set up yet</h2>
        <p className="muted">Full views run on their own domain, which this Bloom doesn't have yet. Commands and cards in chat still work.</p>
      </div>
    );
  }
  if (left) {
    return (
      <div className="app-empty" role="alert">
        <div className="app-empty-icon" aria-hidden="true">⚠️</div>
        <h2>{app.name} tried to leave its page</h2>
        <p className="muted">Apps can't navigate away, so it was stopped.</p>
        <button type="button" className="btn btn-small" onClick={() => setLeft(false)}>Reload the app</button>
      </div>
    );
  }
  return (
    <div className="app-frame-wrap">
      {!loaded && <div className="app-frame-loading" aria-hidden="true"><span className="shimmer">Opening {app.name}…</span></div>}
      <iframe
        key={`${app.slug}:${app.bloom?.bundle ?? ""}:${origin}`}
        ref={frame}
        className={`app-frame${loaded ? " ready" : ""}`}
        title={app.name}
        src={app.bloom ? `${origin}/frame/g/${room.serverId}/${app.bloom.bundle}` : `${origin}/frame/${app.slug}`}
        sandbox={FRAME_SANDBOX}
        referrerPolicy="no-referrer"
        allow=""
        onLoad={() => {
          // The first load is our page. Any later one means the app navigated its frame somewhere else.
          if (++loads.current > 1) return setLeft(true);
          setLoaded(true);
        }}
      />
    </div>
  );
}
